import { ALK_EVENT_TYPE, CONTEXT_INIT_TAB_TYPE, CONTEXT_SPECIFIC_TYPE, CONTEXT_TYPE, FEATURE_PERIMETER } from '../../model/enums';
import { AlkEvent } from '../../framework/events/alk.event';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';

import { WizObjectUtils } from '@wizeoo/akm-core-lib';
import { FilterComponent,  WizRoot, FilterObj, WizEventManager, WizActionObj } from '@wizeoo/akm-component-lib';
import { StorageService } from '../offline/storage.service';
import { UserContextCmd } from '../../command/user/context/user-context.cmd';
import { UserContext, UserContextWrapper} from '@wizeoo/akm-message-lib';
import { Subscription, timer } from 'rxjs';
import { cloneDeep, update } from 'lodash-es';
import { VisionVO } from '@wizeoo/akm-compute-lib';
import { MODE } from '../../components/multi-display/config/multi-display.config';
import { UserVO } from '../../vo/user/user.vo';
import { UserApplicationVO } from '../../vo/user/user-app.vo';

import { MultiDisplay } from '../../components/multi-display/multi-display.component';
import { getDefaultWidgetConfigurationObj } from '../widgets/widget.engine';
import { FeaturePermission } from '../permission/permissions.types';
import { aV, ex } from '@fullcalendar/core/internal-common';
import { TabVO } from '../../vo/workingzone/tab.vo';

/** une clé de contexte pour un filtre (3 params) */
export interface ContextFilterObj {
  basic: string; // le string du filtre de base
  filters: FilterObj[]; // la collection de filtres complémentaires
  open: boolean; // ouvert ou pas
}

/** une clé de contexte pour un filtre (3 params) */
export interface ContextTabObj {
  order: number; // l'ordre dans la barre
}

/** une entrée de contexte */
export interface CtxtEntry {
  
  id?: number; // id remote quand persisté
  contextKey?: string; // clé unique de l'entrée de contexte

  login: string; // login du user
  range: string; // perimetre du contexte CONTEXT_TYPE
  revisionNumber?: number; // version

  domain?: string;
  businessUnitCode?: string;
  vision?: string;

  language?: string; // param user // langue
  selectedTab?: string; // param app, onglet en cours

  widgetId?: string; // UUID du widget
  filter?: ContextFilterObj; // spécifique MULTI_DISPLAY, configuration des filtres standards
  multiDisplayMode?: MODE; // mode d'affichage du multi
  
  tabId?: string; // identifiant unique du TAB
  showLeftPanel?: boolean; // affichage panneau gauche
  showRightPanel?: boolean; // affichage panneau droite
  selectedItemId?: string; // item actif
  
  // pour compatibilité WizContextTabObj
  kind?: string; // 'static' | 'template';
  data?: any; // les données non normalisée du contexte (des params de widget, par exemple), c'est du json 
}

/** chargement du pool local */
interface LocalContext {
  map: Map<string, Map<string, CtxtEntry>>; // map des entrées de contexte par userID | key = contexte
}

/** gestion du contexte utilisateur, stocké dans le storage + cache local */
export class UserContextService {
  
  protected _model: Model = null;
  constructor(model: Model) {
    // console.log('ucs constr', model);
    this._model = model;
  }
  public get model(): Model {
    return this._model as Model;
  }
  
  private _loadedVisions: Map<string, Map<string, VisionVO>> = new Map(); // visions chargées


  private _contextEntries: Map<string, Map<string, CtxtEntry>>; // map des clés de contexte, par login, la clé est normalisée (_buildEntryKey())
  public context: CtxtEntry[] = null; // le contexte complet, à plat... toutes visions confondues, pour le userID

  /** Liste des visions dont le contexte a déjà été chargé */
  public visionContextIsUp(bu: string, visionCode: string): boolean {
    let result: boolean = this._loadedVisions.get(bu)?.has(visionCode);
    // console.log('visionContextIsUp', result, bu, visionCode);
    return result;
  }
  
  /** login du user connecté */
  private get _userLogin(): string {
    const usr: UserVO = this._model.connectedUser;
    return usr?.mail;
  }

  /** chargement du contexte utilisateur local */
  public loadUserContext(): UserContextCmd {

    const localContext: LocalContext = StorageService.getInstance().get<LocalContext>(ViewData.CONTEXT_LOCAL_KEY); // toute la grappe locale...
    
    if(!!localContext?.map) { // bonne génération, fonctionnement en map
      this._contextEntries = localContext?.map;
      // console.log('local entries', this._contextEntries);
    } 
    else {
      this._contextEntries = new Map(); 
    }
    
    // alimentation de la collection à plat
    if (this._contextEntries.has(this._userLogin)) {
      this.context = [...this._contextEntries.get(this._userLogin).values()];
    } else { // mais ce user n'existe pas encore
      this._contextEntries.set(this._userLogin, new Map());
      this.context = [this._getDefaultContextEntry(CONTEXT_TYPE.USER)];
    }

    const numRev: number = this._getLastUpdate();
    const cmd: UserContextCmd =new UserContextCmd(this.model).getContext(this._userLogin, this.backFromRemoteContext.bind(this), numRev);
    return cmd;
  }

  /** retour d'appel remote de contexte pour complétion du local */
  private backFromRemoteContext(cmd: UserContextCmd): void {
    const rawResult: UserContextWrapper = cmd.result;
    const remoteEntries: UserContext[] = rawResult.entities;
    const entries: CtxtEntry[] = remoteEntries.map((vo: UserContext) => this.remoteContextToCtxtEntry(vo));
    
    const entryMap: Map<string, CtxtEntry> = new Map(entries.map(item => [item.contextKey, item]));

    // pas tout charger à chaque fois... console.log('back from remote', entries);
    this.onUpdateContext(entryMap); // mise à jour de la collection locale si nécessaire
    const bu: string = this._model.businessUnitCode;
    const vis: string = this._model.visionCode;
    if(!this._loadedVisions.has(bu)) {
      this._loadedVisions.set(bu, new Map());
    }
    this._loadedVisions.get(bu).set(vis, this._model.currentVision); // la vision est chargée
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.VISION_CONTEXT_LOADED, this._model.currentVision)); // on le signale
  }

  /**
   * explode de la conf JSON d'une entrée de contexte remote
   * @param UserContext
   * @returns CtxtEntry
   */
   private remoteContextToCtxtEntry(vo: UserContext): CtxtEntry { 
    let result: CtxtEntry = vo;
    result.data = vo.data; // JSON.parse(vo.data);
    return result;
  }

  /**
   * retour de chargement de contexte. Ecrase les collections locales avec les données remote
   * @param pEntries les clés à merger
   * @returns la map des entrées de contexte mise à jour
   */
  public onUpdateContext(pEntries: Map<string, CtxtEntry>): CtxtEntry[] { 
    // console.log('onUpdateContext', pEntries);
    if (pEntries?.size > 0) {
      for (const entry of pEntries.values()) {
        if(!this._contextEntries.has(entry.login)) {
          this._contextEntries.set(entry.login, new Map());
        }
        this._contextEntries.get(entry.login).set(entry.contextKey, {...entry}); // {...} perdre l'instance, sinon # select sur widget
      }
      const newContext: CtxtEntry[] = [...this._contextEntries.get(this._userLogin).values()];
      this.context = newContext; // mise à jour du pool du user

      // mise à jour du contexte local
      this._updateLocalStorage(); // mise à jour du cache

      return newContext;
    } 
    return null; // aucune modif
  }

  /**
   * sur changement de vision, chargement si nécessaire
   */
  public onVisionChanged(e: AlkEvent = null) {
    // console.log('user Context service _onVisionChanged', this._model.visionCode, e);
    this.loadUserContext().execute();
  }

  /** Récupération du dernier numéro de révision dans le contexte local (pour le user) */
  private _getLastUpdate(): number {  
    let result: number = 0;
    if (this._contextEntries.has(this._userLogin)) {
      [...this._contextEntries.get(this._userLogin).values()].forEach((ctx: CtxtEntry) => {
        // console.log('getLastUpdate', ctx.contextKey, ctx.revisionNumber);
        if (ctx.revisionNumber > result) {
          result = ctx.revisionNumber;
        } 
      });
    }
    
    return result;
  }

  /** prochain num d'update pour collecte delta */
  private _getNextLastUpdate(): number {
    return this._getLastUpdate() + 1;
  }

  /**
   * alimentation de la structure minimale pour une entrée de contexte
   * Va alimenter user, dom, bu, vision
   * @param perimeter app, tab, widget...
   * @returns prototype de contextEntry
   */
  private _getDefaultContextEntry(range: string): CtxtEntry {
    let result: CtxtEntry = {
      login: this._userLogin,
      domain: this._model.domainCode,
      businessUnitCode: this._model.businessUnitCode,
      vision: this._model.visionCode,
      range,
      revisionNumber: 0
    };

    this._buildEntryKey(result); // on ajoute la clé
    return result;
  }

  /* ################################################
                persistence
    ################################################ */ 

  /**
   * méthode générique d'enregistrement des contextes modifiés
   * @param pContextEntries
   */
  private _save(candidates: CtxtEntry[]) {
    
    if (!this._contextEntries || this._contextEntries === undefined) { // on est sur la page de fallback
      return null;
    }
    
    const entriesToSave: Map<string, CtxtEntry> = new Map(); // les candidats
    const revisionNumber: number = this._getNextLastUpdate();// le numRev cible
  
    candidates?.forEach((candidate: CtxtEntry) =>{ // pas d'enregistrement si c'est le même
      const entryKey: string = candidate.contextKey; //  this._buildEntryKey(candidate);
      const exist: CtxtEntry = this._contextEntries?.get(candidate.login)?.get(entryKey);
      if(!exist || !WizObjectUtils.deepEquals(candidate, exist)) {
        candidate.revisionNumber = revisionNumber;
        entriesToSave.set(candidate.contextKey, {...candidate});
      }
    });
    
    if (entriesToSave.size > 0) {
      // console.log('_save', entriesToSave, revisionNumber);
      this.timeMeImFamous(entriesToSave, revisionNumber);
      // TODO utiliser worker OU persister simplement de temps en temps
      // console.log('remote call usercontext', entriesToSave);
      // new UserContextCmd().updateContext(this._userLogin, entriesToSave, revisionNumber, this._onContextEntrySaved.bind(this)).execute();
    }
  }

  private revisionNumber: number = 0;
  private pendingRemote: Map<string, CtxtEntry> = new Map();

  private syncTimer : Subscription = null;

  /** timer pour éviter les appels remotes simultanés */
  public timeMeImFamous(entriesToSave : Map<string, CtxtEntry>, revisionNumber: number): void {
    this.revisionNumber = revisionNumber;
    [...entriesToSave.values()].forEach(ctxt => {
      this.pendingRemote.set(ctxt.contextKey, ctxt);
    })
    // this.pendingRemote = {...this.pendingRemote, ...entriesToSave};
    // console.log('timeMeImFamous', this.pendingRemote, entriesToSave, revisionNumber);
    // prêt dans 3s
    // this.timesUp(true);
    if(!!this.syncTimer) {
      this.syncTimer.unsubscribe();
    }
    this.syncTimer = timer(3000).subscribe(val => {
      // console.log('timer syncRemote');
      // this.timesUp(false)
      this.syncRemote();
      // this.syncRemote.bind(this);
      this.syncTimer.unsubscribe();
    });

    // écriture en local, tout de suite
    this.onUpdateContext(entriesToSave);
  }

  /** sync remote, on envoie la pile de pendingRemote */
  public syncRemote(): void {
    // console.log('syncRemote', this.pendingRemote);
    let cmd: UserContextCmd = new UserContextCmd(this.model).updateContext(this._userLogin, cloneDeep(this.pendingRemote), this.revisionNumber, this._onContextEntrySaved.bind(this));
    if(!!cmd) {
      cmd.execute();
    }
    // .execute();
    this.pendingRemote = new Map();
  }

  /**
   * sur persistence du contexte, mise à jour du cache local
   * @param pEntries contextes modifiés
   * @param revisionNumber num d'update
   */
  private _onContextEntrySaved(cmd: UserContextCmd) {
    
    const pEntries: Map<string, CtxtEntry> = cmd.entriesToSave; // ... celle d'avant la demande... pas de fallback sur le service
    // console.log('_onContextEntrySaved', pEntries);
    this.onUpdateContext(pEntries); // mise à jour du contexte local
    // this.timesUp(false);
  }

  /**
   * merge du stockage local des contextes (version 2, on passe en Map)
   * @param entries
   */
  private _updateLocalStorage() {
   StorageService.getInstance().set<LocalContext>(ViewData.CONTEXT_LOCAL_KEY, {map:this._contextEntries});
  }

  /**
   * reset du contexte local
   *
   */
  public resetLocalStorage() {
    StorageService.getInstance().remove(ViewData.CONTEXT_LOCAL_KEY); // on rince le storage
    this.loadUserContext().execute(); // on recharge
  }

  /* ################################################
                persistence
    ################################################ */


  /**
   * mise à jour de la configuration de vision
   * @param pContext contient le selectedTab(depuis TabBar) ou le language (depuis TranslationPop)
   */
  saveCurrentVision(pContext: Partial<CtxtEntry>) {
    // console.log('saveCurrentVision', pContext);
    let ctxt: CtxtEntry = {...this.getVisionContext()};
    let newData: any = {...ctxt.data, ...pContext.data};
    ctxt.data = newData; // le diable est dans les détails...
    this._save([ctxt]);
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.TAB_IS_ON, this));
  }

  /** l'ordre est alimenté... a priori */
  saveTabs(tabs: Partial<CtxtEntry>[]) { 
    let result: CtxtEntry [] = [];
    tabs.forEach((tab: Partial<CtxtEntry>) => {
      const ctxt: CtxtEntry  = {
        // tabId: tab.featureCode,
        ...this._getDefaultContextEntry(CONTEXT_TYPE.TAB),
        ...tab,
      }
      // console.log('saveTabs', tab.tabId, tab.data?.order, tab.data?.viewOrder);
      result.push(ctxt);
    });
    
    this._save(result);
  }

  /* ################################################
                accesseurs par périmètres
    ################################################ */


  /** conf de la vision */
  public getVisionContext(): CtxtEntry {
    let visionEntry: CtxtEntry = this._getDefaultContextEntry(CONTEXT_TYPE.APP);
    if (!!this._contextEntries?.get(this._userLogin)?.has(this.buildKey())) {
      visionEntry = this._contextEntries.get(this._userLogin).get(this.buildKey());
    }
    return visionEntry;
  }

  /** chargement de la liste des tabs */
  public getTabs(): CtxtEntry[] {
    let result: CtxtEntry[] = [];
    
    if (!!this._contextEntries?.has(this._userLogin)) {

      const entries = [...this._contextEntries.get(this._userLogin).values()];
      // console.log('entries', entries);
      entries.forEach((ctxt: CtxtEntry) => {
        if (this._checkContextEntryScope(ctxt) && ctxt.range === CONTEXT_TYPE.TAB) {
          const tabE: CtxtEntry = ctxt;
          // console.log('TABS', tabE.tabId, tabE);
          result.push(tabE);
        }  
      });
      
      // Récupération de l'éventuelle entrée de contexte d'initialisation des onglets
      let initTabEntry: CtxtEntry = this._findInitTabEntry();
      // Pas d'onglet...
      if (result.length === 0) {
        // Si on n'a pas fait l'initialisation, on y va...
        if (initTabEntry === null) {
          result = this._initTabsFromPermissions();
        }
        else {
          // Sinon, on complète avec les onglets pour lesquels l'utilisateur à maintenant les droits
          result = this._completeTabsFromPermissions(initTabEntry, result);
        }
      }
      else {
        // Potentiellement, l'utilisateur ne dispose plus de droits sur un onglet issu du contexte
        result = this._revokeTabsFromPermission(result);
        // Potentiellement, l'utilistaeur a maintenant des droits sur de nouveaux onglets 
        result = this._completeTabsFromPermissions(initTabEntry, result);
     }
    }

    return result;
  }

  /**
   * contrôle de cohérence des entrées de contexte : même vision
   * @param ctxt cible
   * @returns true si conforme à la conf en cours
   */
  private _checkContextEntryScope(ctxt: CtxtEntry): boolean {
    let result: boolean = false;
    
    if (ctxt.login === this._userLogin &&
      ctxt.businessUnitCode === this._model.businessUnitCode &&
      ctxt.domain === this._model.domainCode &&
      ctxt.vision === this._model.visionCode
    ) {
      result = true;
    } else {
      // console.log('_checkContextEntryScope bad',ctxt, this._model.businessUnitCode, this._model.domainCode, this._model.visionCode)
    }
    return result;
  }

  /** 
   * Recherche de l'entrée de contexte d'initialisation des onglets 
   * 
   * @returns l'entrée de contexte d'initialisation des onglets si elle existe, null dans le cas contraire
   */
  private _findInitTabEntry(): CtxtEntry {
    let result: CtxtEntry = null;
    if (!!this._contextEntries?.has(this._userLogin)) {
      const entries = [...this._contextEntries.get(this._userLogin).values()];
      let entry: CtxtEntry = entries.find(ctx => ctx.contextKey === this._getDefaultContextEntry(CONTEXT_TYPE.INIT_TAB).contextKey);
      if (entry !== undefined) {
        result = entry;
      }
    }
    return result;
  }

  /** 
   * Initialisation des onglets à partir des rôles
   * 
   * @returns Le tableau des entrées de contexte d'onglet construites à partir des rôles, tableau vide si aucune
   */
  private _initTabsFromPermissions(): CtxtEntry[] {
    let result: CtxtEntry[] = [];
    // Récupération des onglets pour lesquels l'utilisateur dispose des droits
    const tabsFromPermissions: WizActionObj[] = this._findTabsFromPermissions();
    // S'il y en a, on peut mettre à jout le contexte avec ces onglets
    if (tabsFromPermissions.length > 0) {
        // Mise à jour du contexte
        Model.getInstance().tabEngine.updateContextTabs(tabsFromPermissions);
        // Et du coup, on va retourner les onglets du contexte
        result = Model.getInstance().tabEngine.userContextTabs;
        // Et enfin on dispatch l'évènement de contexte chargé
        WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.VISION_CONTEXT_LOADED, this.model.currentVision));
    }

    // Dans tous les cas, on mémorise dans le contexte que l'on est passé par la phase d'init des onglets
    this._saveInitTabEntry(tabsFromPermissions);

    return result;
  }

  /**
   * Mémorisation dans le contexte d'une entrée de contexte d'initialisation des onglets avec les onglets disponibles du moment 
   * ainsi que les onglets pour lesquels l'utilisateur dispose des droits
   * 
   * @param Le tableau des onglets pour lesquels l'utilisateur dispose des droits
   */
  private _saveInitTabEntry(tabsFromPermissions: WizActionObj[]) {
    const initTabEntry: CtxtEntry = this._getDefaultContextEntry(CONTEXT_TYPE.INIT_TAB);
    let data: Map<string, string[]> = new Map();
    data.set(CONTEXT_INIT_TAB_TYPE.AVAIL, Model.getInstance().tabEngine.customTabs.map(tab => this.buildKey(tab.featureCode)));
    data.set(CONTEXT_INIT_TAB_TYPE.PERM, tabsFromPermissions.map(tab => this.buildKey(tab.featureCode)));
    initTabEntry.data = Object.fromEntries(data);
    const initTab: CtxtEntry[] = [initTabEntry];
    this._save(initTab);
  }

  /**
   * Refresh du contexte d'initialisation des onglets avec les onglets disponibles du moment ainsi que ceux pour lesquels l'utilisateur dispose des droits
   */
  public refrehInitTabEntry() {
    this._saveInitTabEntry(this._findTabsFromPermissions());
  }

  /**
   * Détermination des onglets pour lesquels l'utilisateur dispose des droits
   * 
   * @returns Le tableau des onglets identifiés, tableau vide si aucun
   */
  private _findTabsFromPermissions(): WizActionObj[] {
    let result: WizActionObj[] = [];
    // Récupération des onglets disponibles pour le domaine fonctionnel
    const customTabs: WizActionObj[] = Model.getInstance().tabEngine.customTabs;
    // Si pas d'onglets disponibles, inutile d'aller plus loin...
    if (customTabs.length > 0) {
      // Récupération des apps de l'utilisateur sur la vision de contexte
      const visionApp: UserApplicationVO =  Model.getInstance().userEngine.getUserApp(
        this.model.connectedUser,
        Model.getInstance().domainCode,
        Model.getInstance().businessUnitCode,
        Model.getInstance().visionCode,
        FEATURE_PERIMETER.APP,
        true,
        true
      );
      // Récupération des apps de l'utilisateur sur la vision *
      const wildcardVisionApp: UserApplicationVO =  Model.getInstance().userEngine.getUserApp(
        this.model.connectedUser,
        Model.getInstance().domainCode,
        Model.getInstance().businessUnitCode,
        ViewData.defaultVision,
        FEATURE_PERIMETER.APP,
        true,
        true
      );
      // Récupération des droits
      const permissioMap: Map<string, FeaturePermission> = Model.getInstance().userAppEngine.generatePermission(); 
      // On ne travaille que sur les onglets
      const tabPermissionArray: FeaturePermission[] = !!permissioMap ? [...permissioMap.values()].filter(p => p.feature.kind === "TAB") : [];
      tabPermissionArray.forEach(p => {
        // L'utilisateur dispose de droits sur l'onglet ?
        // On commence par regarder les droits au niveau de la vision * (et pas doNone)
        if (this.model.permissionEngine.canRead(p.code, wildcardVisionApp)) {
          customTabs.forEach((wao: WizActionObj) => {
            if(wao.featureCode === p.code) {
              result.push(wao);
            }
          });
        }
        // Puis on complète avec les droits au niveau de la vision de contexte (pas encore ajouté et pas doNone sur la vision *)
        if (this.model.permissionEngine.canRead(p.code, visionApp) && result.find(wao => wao.featureCode === p.code) === undefined && p.roles.filter(r => r.doNone && wildcardVisionApp.hasRole(r)).length === 0) {
          customTabs.forEach((wao: WizActionObj) => {
            if(wao.featureCode === p.code) {
              result.push(wao);
            }
          });
        }
      });
    }
    // Tri par numéros d'ordre puis par libellé
    if (result.length > 0) {
      result = result.sort((a,b) => {
        const aorder = a.viewOrder ?? 0;
        const border = b.viewOrder ?? 0;
        if (aorder !== border) {
          return aorder - border;
        }
        return a.label.localeCompare(b.label);
      });
    }

    return result;
  }

  /**
   * Complétion des onglets avec ceux pour lesquels l'utilisateur dispose maintenant des droits
   * 
   * @param initTabEntry L'éventuelle entrée de contexte d'initialisation des onglets
   * @param existingTabs Les onglets déjà disponible pour le contexte
   * @returns Le tableau des entrées de contexte retalives aux onglets exploités par l'utilisateur
   */
  private _completeTabsFromPermissions(initTabEntry: CtxtEntry, existingTabs: CtxtEntry[]): CtxtEntry[] {
    let result: CtxtEntry[] = existingTabs;

    // Récupération des onglets pour lesquels l'utilisateur dispose des droits
    const tabsFromPermissions: WizActionObj[] = this._findTabsFromPermissions();
    // Détermination des onglets éligibles à la complétion hors ceux déjà présents dans le contexte
    const newContextTabs: WizActionObj[] = tabsFromPermissions.filter(wao => !existingTabs.some(ctx => ctx.tabId === wao.featureCode));

    if (newContextTabs.length > 0) {
      if (initTabEntry === null) {
        // Pas encore eu d'init, donc on y va...
        result = this._updateContextTabs(newContextTabs, tabsFromPermissions);
      } else {
        // On est déjà passé par l'initialisation
        // Dans ce cas, seuls les onglets qui n'étaient pas présents lors de la phase d'initialisation ou ceux pour lesquels l'utilisateur dispose désormais des droits deviennent réellement éligible 
        // Les autres (ceux qui étaient présents lors de la phase d'initialisation) ont de fait été décochés par l'utilisateur, donc inutile de les remettre

        // Récupération des éléments d'init
        const initDatas: Map<string, string[]> = new Map<string, string[]>(Object.entries(initTabEntry.data) as [string, string[]][]);
        const custTabsFromInit: string[] = initDatas.get(CONTEXT_INIT_TAB_TYPE.AVAIL);
        const permTabsFromInit: string[] = initDatas.get(CONTEXT_INIT_TAB_TYPE.PERM);

        // Détermination des onglets à ajouter
        let realNewContextTabs: WizActionObj[] = [];
        newContextTabs.forEach(wao => {
          let tabKey: string = this.buildKey(wao.featureCode);
          if (!custTabsFromInit.includes(tabKey) || !permTabsFromInit.includes(tabKey)) {
            realNewContextTabs.push(wao);
          }
        });
        if (realNewContextTabs.length > 0) {
          // On conserve les onglets issus du contexte, dans le même ordre
          const customTabs: WizActionObj[] = Model.getInstance().tabEngine.customTabs;
          const contextTabs: WizActionObj[] = existingTabs.map(ctx => customTabs.find(wao => wao.featureCode === ctx.tabId)).filter((wao): wao is WizActionObj => wao !== undefined);
          // Tri des onglets à ajouter, par numéro d'ordre puis par libellé
          realNewContextTabs = realNewContextTabs.sort((a,b) => {
            const aorder = a.viewOrder ?? 0;
            const border = b.viewOrder ?? 0;
            if (aorder !== border) {
              return aorder - border;
            }
            return a.label.localeCompare(b.label);
          });
          // Tri de l'ensemble des onglets à mettre dans le contexte
          let tabsToContext: WizActionObj[] = [];
          let i = 0;
          let j = 0;
          while (i < contextTabs.length && j < realNewContextTabs.length) {
            const contextTab = contextTabs[i];
            const newTab = realNewContextTabs[j];
            const ctorder = contextTab.viewOrder ?? 0;
            const ntorder = newTab.viewOrder ?? 0;
            if (ctorder < ntorder || (ctorder === ntorder && contextTab.label.localeCompare(newTab.label) <=0)) {
              tabsToContext.push(contextTab);
              i++
            }
            else {
              tabsToContext.push(newTab);
              j++;
            }
          }
          tabsToContext.concat(contextTabs.slice(i)).concat(realNewContextTabs.slice(j));

          // Mise à jour le contexte
          result = this._updateContextTabs(tabsToContext, tabsFromPermissions);
        }
      }
    }

    return result;
  }

  /**
   * Mise à jour des onglets du contexte
   * 
   * @param contextTabs La liste des onglets sélectionnés
   * @param tabsFromPermissions Le tableau des onglets pour lesquels l'utilisateur a les droits
   * @returns Le tableau des entrées de contexte retalives aux onglets fournis
   */
  private _updateContextTabs(contextTabs: WizActionObj[], tabsFromPermissions: WizActionObj[])
  {
    // Mise à jour du contexte
    Model.getInstance().tabEngine.updateContextTabs(contextTabs);

    // On va retourner les entrées de contexte relatives aux onglets founis 
    let result: CtxtEntry[] = Model.getInstance().tabEngine.userContextTabs;
    // On dispatch l'évènement de contexte chargé
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.VISION_CONTEXT_LOADED, this.model.currentVision));

    // Et enfin, on crée ou met à jour l'entrée de contexte d'initialisation des onglets avec ceux disponibles au moment
    this._saveInitTabEntry(tabsFromPermissions);
    return result;
  }

  /**
   * Mise à jour du contexte si l'utilisateur ne dispose plus de droits sur un onglet issu du contexte
   * 
   * @param existingTabs Les onglets issus du contexte
   * @returns Le tableau des onglets du contexte, éventuellement mis à jour
   */
  private _revokeTabsFromPermission(existingTabs: CtxtEntry[]): CtxtEntry[] {
    let result: CtxtEntry[] = existingTabs;
    // Récupération des onglets disponibles pour le domaine fonctionnel
    const customTabs = Model.getInstance().tabEngine.customTabs;
    // Récupération des ongles pour lesquels l'utilisateur a des droits
    const tabsFromPermissions: WizActionObj[] = this._findTabsFromPermissions();

    // Collecte des entrées de contexte pour lesquelles l'utilisateur n'a plus les droits
    let entriesToRevoke: CtxtEntry[] = [];
    existingTabs.forEach(ctx => {
      // On ne touche pas aux onglets statiques, seules les apps nous intéressent
      let tab: WizActionObj =  customTabs.find(wao => wao.featureCode == ctx.tabId);
      if (!!tab && tab.kind === ViewData.KIND_DYNAMIC && tabsFromPermissions.find(wao => wao.featureCode == ctx.tabId) === undefined) {
        // L'utilisateur ne dispose plus des droits
        entriesToRevoke.push(ctx);
      }
    });
    // Il y a des entrées à supprimer, il faut mettre à jour le contexte
    if (entriesToRevoke.length > 0) {
      entriesToRevoke.forEach(ctx => this.delete(ctx));
      const tabsToConserve: WizActionObj[] = customTabs.filter(wao => existingTabs.some(ctx => ctx.tabId === wao.featureCode)).filter(wao => !entriesToRevoke.some(ctx => ctx.tabId === wao.featureCode));
      result = this._updateContextTabs(tabsToConserve, tabsFromPermissions);
    }

    return result;
  }

  /**
   * tabContext du dernier tab actif
   */
  public getCurrentTab(): CtxtEntry {
    return this.getTab(ViewData.currentTab?.featureCode);
  }

  /**  conf d'un tab par son id */
  getTab(tabId: string): CtxtEntry {
    let tabEntry: CtxtEntry = this._getDefaultContextEntry(CONTEXT_TYPE.TAB);
    tabEntry.tabId = tabId;
    tabEntry.contextKey = this.buildKey(tabId);
    
    if (!!this._contextEntries?.get(this._userLogin)?.has(tabEntry.contextKey)) {
      tabEntry = this._contextEntries.get(this._userLogin).get(tabEntry.contextKey);
    }
    
    return tabEntry;
  }


  /* ################################################
              contextes de widgets
    ################################################ */

  /**
   * sauvegarde de la conf d'un widget (depuis widgetBasic)
   * @param widgetId id unique
   * @param targetTabId tab
   * @param widget la conf du widget

   */
  public saveWidget(widgetId: string, targetTabId: string, wco: any) { 
    if (wco) { // fonctionner avec une vrai conf de widget
      const toSave: CtxtEntry = {widgetId, ...this.getWidget(targetTabId, widgetId)};
      toSave.data = {...wco}; // la conf du widget dans data
      // console.log('saveWidget',targetTabId, toSave.key, wco);
      this._save([toSave]);
    }
  }

  /** 
   * accès à la conf d'un multidisplay
   * 
   * @param targetTabId id unique de la vue
   * @param widgetId id unique du widget (dans la vue)
   * @param spécific, un multidisplay ou filter
   * @returns 
   */
   public getWidget( tabId: string, widgetId: string, specific: boolean = false): CtxtEntry {
    // conf par défaut du widget
    let result: CtxtEntry = {tabId, widgetId, ...this._getDefaultContextEntry(CONTEXT_TYPE.WIDGET)};
    if(!specific) { // tout sauf les multis et filters
      result.data = {...getDefaultWidgetConfigurationObj()};
    }
    if (tabId) {
      result.contextKey = this.buildKey(tabId, widgetId);
      if (!!this._contextEntries?.get(this._userLogin)?.has(result.contextKey)) {
        result = {...this._contextEntries.get(this._userLogin).get(result.contextKey)}; // {...} pour perdre l'instance, sinon pas d'update
      }
    } else {
      // console.log('no tabID on widget', widgetId);
    }
    // console.log('getWidget',result.key, result.data);
    return result;
  }


  /** gestion du mode d'affichage des multi-display */
  public getMultiDisplayMode(mdc: MultiDisplay<any>): MODE {
    // prise du tabId
    const tabId: string = ViewData.currentTab?.featureCode;

    if (!tabId) { // 2ème chance
      const currentTabContext: CtxtEntry = this.getCurrentTab();
    }

    const code: string = !!mdc.code ? mdc.code : CONTEXT_SPECIFIC_TYPE.MULTI_DISPLAY; // un code spécifique, sinon le MAIN
    // collecte de l'existant
    const exist = this.getWidget(tabId, code, true);
    // console.log('getMultiDisplayMode', mdc?.code, exist?.data?.multiDisplayMode, tabId);
    return exist?.data?.multiDisplayMode;
  }

  /**
   * persistence de la conf d'un multidisplay
   * @param mdc MultiDisplay cible
   */
  public saveMultiDisplayMode(mdc: MultiDisplay<any>):void {
    // prise du tabId
    const tabId: string = ViewData.currentTab?.featureCode;
    const code: string = !!mdc.code ? mdc.code : CONTEXT_SPECIFIC_TYPE.MULTI_DISPLAY; // un code spécifique, sinon le MAIN
    // console.log('saveMultiDisplayMode', mdc?.code, mdc?.currentMode, tabId);
    
    if(!!tabId) {
      // collecte de l'existant
      const toSave: CtxtEntry = {...this.getWidget(tabId, code, true)};
      // mode d'affichage à persister
      const multiDisplayMode: MODE = mdc.currentMode;
      toSave.data = {multiDisplayMode: multiDisplayMode};

      this._save([toSave]);
    }
  }


  /** persistence d'un contexte de filtre
   * 
   * @param pFilterComponent le composant source
   * @param code son nom éventuel. Sinon, c'est un MAIN_FILTER
   */
  saveLocalFilter(filter: FilterComponent, code: string = CONTEXT_SPECIFIC_TYPE.MAIN_FILTER) {
    // prise du tabId
    const tabId: string = ViewData.currentTab?.featureCode;
    // console.log('saveLocalFilter', code, tabId, filter.getBasic());

    if(!!tabId) {
      // collecte de l'existant
      const toSave: CtxtEntry = {...this.getWidget(tabId, code, true)};
      // alimentation des params de filtre
      const cfo: ContextFilterObj = {basic: filter.getBasic(), filters: filter.getFilterKeys(), open: filter.getOpen()};
      toSave.data = {filter: cfo};

      this._save([toSave]);
    } 
  }

  /**
   * alimentation des données du filtre depuis le contexte.
   * @param filter: le composant de filtre. ATTENTION, les champs de filtre doivent être alimentés AVANT
   * @param componentName : pour les compos non Main
   */
  public feedFilterContext(filter: FilterComponent, code: string = CONTEXT_SPECIFIC_TYPE.MAIN_FILTER): void {
    // demande de contexte sur filter
    
    const tabId: string = ViewData.currentTab?.featureCode;
    
    // collecte de l'existant
    const exist = this.getWidget(tabId, code, true);
    
    const contextFilterObj: ContextFilterObj = exist?.data?.filter;

    // console.log('feedFilterContext', code, tabId, filter.getBasic(), !!exist, contextFilterObj?.basic);
    if (contextFilterObj) {
      filter.applyContext(contextFilterObj.basic, contextFilterObj.filters, contextFilterObj.open).build(true);
    } else {
      filter.applyContext(null, null, false).build(true);
    }
  }




  public deleteTab(tabId: string) {
    // console.log('ucs deleteTab', tabId);
    const fromTab: CtxtEntry = { tabId, ...this._getDefaultContextEntry(CONTEXT_TYPE.TAB)} as CtxtEntry;
    this._buildEntryKey(fromTab); // on l'alimente correctement
    const exist: CtxtEntry = this._contextEntries?.get(this._userLogin)?.get(fromTab.contextKey);
    // console.log('ucs deleteTab2', fromTab, exist);
    this.delete(exist);
  }

  public delete(pEntry: CtxtEntry) {
    if (pEntry) {
      const toDie: CtxtEntry = pEntry;
      // console.log('toDie entry', toDie);
      new UserContextCmd(this.model).deleteContext(toDie, this._onContextDeleted.bind(this)).execute();
    }
  }

  /** callback sur suppression de contexte */
  private _onContextDeleted(cmd: UserContextCmd) {
    const pEntry: CtxtEntry = cmd.toDeleteEntry;
    const entryKey: string = this._buildEntryKey(pEntry);
    if (entryKey && this._contextEntries.get(this._userLogin)?.has(entryKey)) {
      this._contextEntries.get(this._userLogin).delete(entryKey);
      const newContext: CtxtEntry[] = [...this._contextEntries.get(this._userLogin).values()];
      this.context = newContext;
      // TODO dispatch modif ?? this._submitNewContext(newContext);
      this._updateLocalStorage();
    }
  }

  /* ################################################
            construction des clés uniques
    ################################################ */

  /**
   * construction d'une clé de contexte selon son périmètre (app, tab, widget...)
   * @param pEntry
   * @returns la clé
   */
  private _buildEntryKey(pEntry: CtxtEntry): string {
    // let user: string = pEntry.userId || this._model.connectedUser.uid;
    let domain: string = pEntry.domain || this._model.domainCode;
    let bu: string = pEntry.businessUnitCode || this._model.businessUnitCode;
    let vision: string = pEntry.vision || this._model.visionCode;

    let keyParts: string[] = [domain, bu, vision]; // [user, domain, bu, vision];

    switch (pEntry.range) {
      case CONTEXT_TYPE.WIDGET:
        if(!!pEntry.tabId) {
          keyParts.push(pEntry.tabId);
        }
        if(!!pEntry.widgetId) {
          keyParts.push(pEntry.widgetId);
        }
        break;
      case CONTEXT_TYPE.TAB:  
        if(!!pEntry.tabId) {
          keyParts.push(pEntry.tabId);
        }
        break;
      case CONTEXT_TYPE.INIT_TAB:
        keyParts.push(CONTEXT_TYPE.INIT_TAB);
        break;
      default:
        break;      
    }
    // console.log('build entryKey', pEntry.perimeter, key);
    let key: string = keyParts.join(ViewData.WIDGET_KEY_SEPARATOR);
    pEntry.contextKey = key;

    pEntry.login = this._model.connectedUser.mail;

    return key;
  }

  /**
   * construction d'une clé de contexte , selon la portée
   *
   * @param pTabId clé de tab éventuelle
   * @param pWidgetId clé de widget éventuelle
   * @returns la clé
   */
  public buildKey(pTabId?: string, pWidgetId?: string): string {
    let keyParts: string[] = [this._model.domainCode, this._model.businessUnitCode, this._model.visionCode];
    if(!!pTabId) {
      keyParts.push(pTabId);
    }
    if(!!pWidgetId) {
      keyParts.push(pWidgetId);
    }
    let key: string = keyParts.join(ViewData.WIDGET_KEY_SEPARATOR);
    
    return key;
  }

  /* encapsulation d'un contexte générique en VO pour persistence
  * @param param0 le contexte
  * @param revisionNumber numRev
  * @returns un ContextEntryVO
  */
 public ctxtEntryToRemote( ctxt: CtxtEntry, revNumber: number): UserContext {
     const result: UserContext =  new UserContext();
     result.id = ctxt.id;
     result.contextKey = ctxt.contextKey;
     result.login = ctxt.login;
     result.range = ctxt.range;
     result.revisionNumber = revNumber;

     result.domain = ctxt.domain;
     result.businessUnitCode = ctxt.businessUnitCode;
     
     result.vision = ctxt.vision;
     result.tabId = ctxt.tabId;
     result.widgetId = ctxt.widgetId;

     result.data = JSON.stringify(ctxt.data);
     
     return result;
 }

}
